/**
 * Opening-hours row — a `KbItem` that renders as a 7-day schedule
 * instead of a plain key/value line. Port of
 * `fe-prototype/build/KnowledgeBase.jsx::HoursRow`.
 *
 * The `value` field is stored as plain English (e.g.
 * "Mon-Fri 7am-5pm, Sat 8am-12pm") and parsed client-side by
 * `lib/hours.ts::parse` into the 7-row schedule grid. The editor
 * shows a live preview of the schedule as the visitor types, so a
 * malformed input is visually obvious before save.
 *
 * A KB item is rendered through this component when its `key`
 * matches the hours heuristic (see `isHoursItem` in `KbDisplay.tsx`),
 * so the backend doesn't need to mark hours items with a discriminator
 * — the SPA opts in by name.
 */

import { useState } from "react";

import { Icon } from "./Icon.js";
import type { KbItem } from "./types.js";
import { parse } from "../lib/hours.js";

interface HoursRowProps {
  item: KbItem;
  onSave: (value: string) => Promise<void> | void;
  saving: boolean;
}

export function HoursRow({ item, onSave, saving }: HoursRowProps) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.value);
  const schedule = parse(item.value);
  const draftSchedule = parse(text);

  const commit = async () => {
    await onSave(text);
    setEditing(false);
  };

  const cancel = () => {
    setText(item.value);
    setEditing(false);
  };

  return (
    <div className="kb-item" data-testid={`hours-row-${item.key}`}>
      <div className="kb-k">{item.key}</div>
      <div className="kb-v">
        {editing ? (
          <div className="hours-edit">
            <input
              className="kb-edit"
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void commit();
                if (e.key === "Escape") cancel();
              }}
              placeholder="e.g. Mon–Fri 7am–5pm, Sat 8am–12pm"
              aria-label="Opening hours, plain English"
            />
            <div className="hint">
              <Icon name="wand-2" size={13} />
              Type ranges in plain English, we'll set the week for you.
            </div>
            <div className="hours-preview">
              <div className="pv-h">
                <Icon name="calendar" size={12} />
                This sets
              </div>
              <div className="sched">
                {draftSchedule.map((s) => (
                  <div
                    key={s.day}
                    className={`sched-row${s.closed ? " closed" : ""}`}
                  >
                    <span className="d">{s.day}</span>
                    <span className="t">
                      {s.closed ? "Closed" : `${s.open}–${s.close}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="sched">
              {schedule.map((s) => (
                <div
                  key={s.day}
                  className={`sched-row${s.closed ? " closed" : ""}`}
                >
                  <span className="d">{s.day}</span>
                  <span className="t">
                    {s.closed ? "Closed" : `${s.open}–${s.close}`}
                  </span>
                </div>
              ))}
            </div>
            <div className="src" style={{ marginTop: 6 }}>
              {item.status === "needs_you" ? (
                <span className="status-pill review">needs you</span>
              ) : (
                <span className="status-pill confirmed">
                  <Icon name="check" size={11} stroke={3} />
                  confirmed
                </span>
              )}
              <span style={{ marginLeft: 8 }}>from {item.source}</span>
            </div>
          </>
        )}
      </div>
      <div className="kb-actions">
        {editing ? (
          <button
            type="button"
            className="icon-btn ok"
            onClick={() => void commit()}
            disabled={saving}
            aria-label="Save"
          >
            <Icon name="check" size={16} stroke={2.6} />
          </button>
        ) : (
          <button
            type="button"
            className="icon-btn"
            onClick={() => {
              setText(item.value);
              setEditing(true);
            }}
            aria-label="Edit"
          >
            <Icon name="pencil" size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Heuristic for "is this KB item the opening-hours one?". Matches the
 * common keys our pipeline uses ("Opening hours", "Hours", "Trading
 * hours") plus the group-name fallback so a "Hours & availability"
 * group's children all get the schedule renderer if their key looks
 * like a time.
 */
export function isHoursItem(groupName: string, itemKey: string): boolean {
  const k = itemKey.toLowerCase();
  if (k === "opening hours" || k === "hours" || k === "trading hours") {
    return true;
  }
  if (k.includes("hours") && groupName.toLowerCase().includes("hour")) {
    return true;
  }
  return false;
}
